// The web's mirror of the receipt layout's rules: validateSections and the
// SectionTakes* rules in internal/core/domain/printing_settings.go (#637).
// See pricing.ts for why these exist twice.
//
// Settings → Printing refuses a move or an addition here first, so the owner
// sees why at once instead of after a save; the server still validates every
// save, and when a vector fails, this file is wrong, never the vector.

import type { components } from '../api/types';

export type ReceiptSection = components['schemas']['ReceiptSection'];
export type SectionKind = ReceiptSection['kind'];

/** A section as the rules read it, which may name a kind the receipt does not have. */
export interface SectionShape {
	kind: string;
	visible?: boolean;
	text?: string;
	style?: string;
	spaceMM?: number;
	widthPercent?: number;
}

/** Receipt section bounds (owner, #637). */
export const MAX_TEXT_LINES = 10;
export const MAX_SEPARATORS = 10;
export const MAX_TEXT_RUNES = 240;
export const MAX_SPACE_MM = 10;

/** The logo's width as a share of the printable width: 10% to 100% in steps of 5 (#949). */
export const MIN_LOGO_PERCENT = 10;
export const MAX_LOGO_PERCENT = 100;
export const LOGO_PERCENT_STEP = 5;

/**
 * Whether a logo's width is unset (0 or absent, the logo at its own size) or
 * a step of LOGO_PERCENT_STEP from MIN_LOGO_PERCENT to MAX_LOGO_PERCENT, as
 * LogoWidthAllowed reads it.
 */
export function logoWidthAllowed(percent: number | undefined): boolean {
	const n = percent ?? 0;
	if (n === 0) return true;
	return (
		Number.isInteger(n) &&
		n >= MIN_LOGO_PERCENT &&
		n <= MAX_LOGO_PERCENT &&
		n % LOGO_PERCENT_STEP === 0
	);
}

/** The kinds that appear exactly once, in the default order. */
export const FIXED_SECTIONS: readonly SectionKind[] = [
	'logo',
	'shopName',
	'shopAddress',
	'shopPhone',
	'orderDetails',
	'items',
	'totals',
	'payment',
	'qr',
];

const LIST = 'receipt.sections';
const MAX_SECTIONS = FIXED_SECTIONS.length + MAX_TEXT_LINES + MAX_SEPARATORS;

function known(kind: string): boolean {
	return kind === 'text' || kind === 'separator' || (FIXED_SECTIONS as string[]).includes(kind);
}

/** What was sold and paid always prints: the order details, items, totals and payment. */
export function alwaysPrinted(kind: string): boolean {
	return kind === 'orderDetails' || kind === 'items' || kind === 'totals' || kind === 'payment';
}

/** Every kind but a separator and the money columns can be centred. */
export function takesCenter(kind: string): boolean {
	return known(kind) && !['separator', 'items', 'totals', 'payment'].includes(kind);
}

/** The shop lines and text lines can print bold. */
export function takesBold(kind: string): boolean {
	return ['shopName', 'shopAddress', 'shopPhone', 'text'].includes(kind);
}

/** Every kind but what always prints can be hidden. */
export function canHide(kind: string): boolean {
	return known(kind) && !alwaysPrinted(kind);
}

/** Text lines and separators are added and deleted; the fixed kinds stay. */
export function removable(kind: string): boolean {
	return kind === 'text' || kind === 'separator';
}

/**
 * The field PrintSettings.Validate names for a section list it refuses, in
 * its own order, or "" when it takes the list.
 */
export function sectionError(sections: readonly SectionShape[]): string {
	const item = (i: number, key: string) => `${LIST}[${i}].${key}`;
	if (sections.length > MAX_SECTIONS) return LIST;
	const seen = new Map<string, number>();
	for (const [i, s] of sections.entries()) {
		if (!known(s.kind)) return item(i, 'kind');
		// Characters, as Go counts runes: a code point, never a UTF-16 unit.
		const text = s.text ?? '';
		if (s.kind === 'text' && ([...text].length > MAX_TEXT_RUNES || text.includes('\u0000'))) {
			return item(i, 'text');
		}
		if (s.kind === 'separator') {
			const style = s.style ?? '';
			if (style !== '' && style !== 'dashed' && style !== 'blank') return item(i, 'style');
			const space = s.spaceMM ?? 0;
			if (space < 0 || space > MAX_SPACE_MM) return item(i, 'spaceMM');
		}
		if (s.kind === 'logo' && !logoWidthAllowed(s.widthPercent)) return item(i, 'widthPercent');
		if (alwaysPrinted(s.kind) && !s.visible) return item(i, 'visible');
		seen.set(s.kind, (seen.get(s.kind) ?? 0) + 1);
	}
	if (FIXED_SECTIONS.some((kind) => seen.get(kind) !== 1)) return LIST;
	if ((seen.get('text') ?? 0) > MAX_TEXT_LINES || (seen.get('separator') ?? 0) > MAX_SEPARATORS) {
		return LIST;
	}
	if (!inOrder(sections)) return LIST;
	return '';
}

/** Whether the items come before the totals, and the totals before the payment. */
export function inOrder(sections: readonly SectionShape[]): boolean {
	// The last place each holds, as SectionsInOrder reads it.
	const at = (kind: string) => {
		let last = -1;
		sections.forEach((s, i) => {
			if (s.kind === kind) last = i;
		});
		return last;
	};
	return at('items') <= at('totals') && at('totals') <= at('payment');
}

/**
 * The sections with the one at `from` put at `to`, or null when either end
 * is off the list or the move breaks the order of the items, totals and
 * payment, which then stays as it was.
 */
export function moveSection<T extends SectionShape>(
	sections: readonly T[],
	from: number,
	to: number,
): T[] | null {
	if (from < 0 || from >= sections.length || to < 0 || to >= sections.length) return null;
	const next = [...sections];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved as T);
	return inOrder(next) ? next : null;
}

/** Whether another text line or separator fits. */
export function canAdd(sections: readonly SectionShape[], kind: 'text' | 'separator'): boolean {
	const count = sections.filter((s) => s.kind === kind).length;
	return count < (kind === 'text' ? MAX_TEXT_LINES : MAX_SEPARATORS);
}
